import { Box } from '@mui/material'
import React, { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import Card from '@/components/Card'
import ColorSelection from '@/components/ColorSelection'
import LabelWithTooltip from '@/components/LabelWithTooltip'
import Select from '@/components/Select'
import { imageType } from '@/pages/getRecipe/allInOne/utils/constants'
import { Metadata } from '@/pages/stainingStudio/crown/StaingingStudio'
import { useAppSelector } from '@/store'
import { useGetMetadataQuery } from '@/store/apiService'
import {
  setDentinCeramicBrand,
  setSelectedDentinColorKey,
} from '@/store/pfmCrown'
import { REGION } from '@/utils/constants'
import { getOptions } from '@/utils/helper'

function DentinPanel({
  containerRef,
  regenerateRecipe,
}: {
  containerRef: React.RefObject<HTMLDivElement>
  regenerateRecipe: (region: string, colorData: any) => void
}) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'dentin',
  })
  const { t: translate } = useTranslation()
  const dispatch = useDispatch()

  const { data: metadata } = useGetMetadataQuery(undefined, {
    refetchOnMountOrArgChange: true,
  }) as {
    data: Metadata | undefined
    error: any
    isLoading: boolean
  }
  const ceramicBrands = useMemo(
    () => (metadata ? getOptions(metadata, 'available_ceramic_brands') : []),
    [metadata],
  )

  const dentinCeramicBrand = useAppSelector(
    (state) => state.pfmCrown.dentinCeramicBrand,
  )

  const { control, setValue, clearErrors } = useForm({
    defaultValues: {
      dentinCeramicBrand: dentinCeramicBrand,
    },
    mode: 'onChange',
  })
  const canvasImage = useAppSelector((state) => state.app.canvasImage)

  const selectedDentinColorKey = useAppSelector(
    (state) => state.pfmCrown.selectedDentinColorKey,
  )

  const dentinColor = useAppSelector((state) => state.pfmCrown.dentinColor)

  const dentinColorAsArray = useAppSelector(
    (state) => state.pfmCrown.DentinColorState,
  )

  return (
    <Box
      display="flex"
      flexDirection="column"
      flexGrow={1}
      gap={1}
      overflow="auto"
    >
      <Card>
        <Select
          options={ceramicBrands}
          title={t('ceramicBrand')}
          tooltipText={t('ceramicBrandTooltip')}
          control={control}
          name="dentinCeramicBrand"
          onFormValueChange={(name, value) => {
            dispatch(setDentinCeramicBrand(value))
          }}
          optionDisabledChecker={(item) => {
            return (
              (canvasImage?.type === imageType.OPTISHADE &&
                !item.optishade_supported) ||
              (canvasImage?.type === imageType.RAW && !item.camera_supported)
            )
          }}
        />
      </Card>
      <Card>
        <LabelWithTooltip
          tooltipText={t('dentinColorTooltip')}
          label={t('dentinColor')}
        />
        {Object.keys(REGION)?.map((r, i) => {
          const currentTarget = dentinColor?.[canvasImage?.id]?.[r]?.targetcolor
          const newTarget = dentinColorAsArray?.[canvasImage?.id]?.[r]

          const isSameTarget =
            !currentTarget || !newTarget
              ? true
              : currentTarget.length !== newTarget.length
                ? false
                : currentTarget.every((val, idx) => val === newTarget[idx])

          return (
            <ColorSelection
              control={control}
              key={i}
              name={r}
              title={translate(REGION[r as keyof typeof REGION])}
              error={false}
              clearErrors={clearErrors}
              setValue={setValue}
              data={dentinColor?.[canvasImage?.id]?.[r]?.targetcolor}
              selectedPickerKey={selectedDentinColorKey}
              onclick={() => dispatch(setSelectedDentinColorKey(r))}
              containerRef={containerRef}
              snackbarClose={!selectedDentinColorKey}
              hasRecipe={!!dentinColor?.[canvasImage?.id]?.[r]?.targetcolor}
              regenerateRecipe={() =>
                regenerateRecipe(r, dentinColorAsArray?.[canvasImage?.id])
              }
              disableButton={isSameTarget}
            />
          )
        })}
      </Card>
    </Box>
  )
}

export default DentinPanel
